import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { LeagueReadiness } from "../components/LeagueReadiness";
import { LeagueCalendar } from "../components/LeagueCalendar";

const usd = (n) =>
  Math.abs(n || 0) >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`;

const List = ({ label, items, accent, testId }) => (
  <div data-testid={testId}>
    <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{label}</p>
    <ul className="mt-4 space-y-3">
      {items.map((t) => (
        <li key={t} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
          <span className="mt-[7px] h-1.5 w-1.5 shrink-0" style={{ background: accent }} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function GovernorsLeague() {
  const [d, setD] = useState(null);

  useSeo({
    title: "The Governors' League — KickStartKenyaFootball",
    description:
      "KPL's proposed county competition across all 47 counties: the format, the venues each county would need, and what a season would cost per fixture, per county and nationally.",
  });

  useEffect(() => {
    api.get("/governors-league").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="governors-league-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="governors-league-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">A proposal, not a programme</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-8 max-w-3xl">{d.standfirst}</p>
          <p className="mt-6 max-w-3xl border-l-2 border-maasai pl-5 text-sm leading-relaxed text-zinc-400"
            data-testid="gl-status">
            {d.status}
          </p>
        </Reveal>

        <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="gl-headlines">
          {[
            ["Cost of one fixture", `$${d.usd_per_fixture}`, "Officials, pitch, medical, kit and travel"],
            ["Fixtures costed a county season", d.fixtures_per_county_season, "One matchday programme plus play-offs"],
            ["One county season", usd(d.usd_per_county_season), "At our own Kenyan rates"],
            ["All 47 counties, one season", usd(d.usd_all_47_season), "The whole competition, once"],
          ].map(([label, value, note], i) => (
            <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`gl-headline-${i}`}>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
              <p className="mt-4 font-display text-xl font-bold leading-none text-white md:text-2xl">{value}</p>
              <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-3">
          <List label="What it is" items={d.what_it_is} accent="#E32118" testId="gl-what-it-is" />
          <List label="Why it fits these facilities" items={d.why_it_fits} accent="#E5A91E" testId="gl-why-it-fits" />
          <List label="Why now" items={d.why_now || []} accent="#4b8f6f" testId="gl-why-now" />
        </div>

        <section className="mt-16" data-testid="gl-format">
          <h2 className="display-3">How A Season Would Run</h2>
          <div className="mt-6 border-t border-zinc-900">
            {(d.format || []).map((f) => (
              <div key={f.label} className="grid gap-3 border-b border-zinc-900 py-5 md:grid-cols-[260px_1fr]">
                <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">{f.label}</p>
                <p className="text-sm leading-relaxed text-zinc-400">{f.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="gl-venues">
          <h2 className="display-3">The Venues Each County Would Need</h2>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">
            The competition only works where there is somewhere to play it. These are the venue classes a
            county would need in place, and every one of them is already costed in the facility library.
          </p>
          <div className="mt-6 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[820px] text-left" data-testid="gl-venue-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Venue", "Per county", "What it hosts", "Specification", ""].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d.venues || []).map((v) => (
                  <tr key={v.venue} className="border-b border-zinc-900 align-top"
                    data-testid={`gl-venue-${v.venue.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <td className="px-4 py-4 text-sm text-white">{v.venue}</td>
                    <td className="mono px-4 py-4 text-sm text-ochre">{v.per_county}</td>
                    <td className="px-4 py-4 text-xs leading-relaxed text-zinc-300">{v.use}</td>
                    <td className="px-4 py-4 text-xs leading-relaxed text-zinc-400">{v.spec}</td>
                    <td className="px-4 py-4">
                      <Link to={v.link} className="mono text-[0.58rem] uppercase tracking-[0.14em] text-ochre hover:text-white">
                        See it costed
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16" data-testid="gl-budget">          <h2 className="display-3">What A Season Would Cost</h2>
          <div className="mt-6 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[560px] text-left" data-testid="gl-budget-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Season cost line", "Per fixture", "Per county season"].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d.season_cost || []).map((r) => (
                  <tr key={r.line} className="border-b border-zinc-900">
                    <td className="px-4 py-3.5 text-sm text-white">{r.line}</td>
                    <td className="mono px-4 py-3.5 text-xs text-zinc-300">${r.usd_per_fixture}</td>
                    <td className="mono px-4 py-3.5 text-xs text-zinc-300">${r.usd_per_season.toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="bg-[#140406]">
                  <td className="px-4 py-3.5 text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white">
                    {d.fixtures_per_county_season} fixtures a county season
                  </td>
                  <td className="mono px-4 py-3.5 text-sm text-white">${d.usd_per_fixture}</td>
                  <td className="mono px-4 py-3.5 text-sm text-ochre">{usd(d.usd_per_county_season)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400" data-testid="gl-all-47">
            All 47 counties, one season: {usd(d.usd_all_47_season)}
          </p>
          <p className="mt-4 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.note}</p>
          {d.source ? (
            <a href={d.source.url} target="_blank" rel="noreferrer"
              className="mono mt-5 inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
              data-testid="gl-source">
              {d.source.label} <ExternalLink size={12} />
            </a>
          ) : null}
        </section>

        <LeagueReadiness />

        <LeagueCalendar />

        <div className="mt-14 flex flex-wrap gap-4">
          <Link to="/plans?track=national&pillar=grassroots" className="btn-primary" data-testid="gl-to-plans">
            The recommended steps behind it <ArrowRight size={14} />
          </Link>
          <Link to="/counties/rollup" className="btn-ghost" data-testid="gl-to-rollup">The national rollup</Link>
          <Link to="/facilities" className="btn-ghost" data-testid="gl-to-facilities">The facility library</Link>
        </div>
      </div>
    </div>
  );
}
